import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, apiRequest } from "../../shared/api/client";
import { clearOfflineSnapshot, readOfflineSnapshot, saveOfflineGrant, saveOfflineSnapshot, prepareOfflineShell } from "./sarf39-offline";
import { currentPlanDayKey, plannedQuantityForDay, scheduledQuantity, buildTree, dayOrder, type Sarf39Snapshot, type Product, type PlannedDay } from "./sarf39-model";
import { parseSarf39Snapshot, parseSarf39Access } from "./sarf39-contract";

export function useSarf39Workspace() {
  const [snapshot, setSnapshot] = useState<Sarf39Snapshot | null>(null);
  const [error, setError] = useState("");
  const [pinRequired, setPinRequired] = useState(false);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [isSubmittingPin, setIsSubmittingPin] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedCode, setSelectedCode] = useState("");
  const [calculationQuantity, setCalculationQuantity] = useState<number | null>(null);
  const [selectedDayKey, setSelectedDayKey] = useState<string | null>(null);
  const [selectionPanelOpen, setSelectionPanelOpen] = useState(false);

  const mounted = useRef(false);
  const generation = useRef(0);
  const lifecycle = useRef(0);
  const pinPending = useRef(false);
  const loadSnapshot = useCallback(async () => {
    const request = ++generation.current;
    const isCurrent = () => mounted.current && generation.current === request;
    try {
      const data = parseSarf39Snapshot(await apiRequest<unknown>("/api/sarf39/data", { cache: "no-store" }));
      if (!isCurrent()) return;
      void saveOfflineSnapshot(data).catch(() => undefined);
      void prepareOfflineShell().catch(() => undefined);
      const defaultDayKey = currentPlanDayKey(data.products);
      const defaultProducts = defaultDayKey
        ? data.products.filter((product) => plannedQuantityForDay(product, defaultDayKey) > 0)
        : data.products;
      const defaultProduct = defaultProducts[0] ?? data.products[0];
      setSnapshot(data);
      setSelectedCode((current) => current || defaultProduct?.code || "");
      setCalculationQuantity((current) => current ?? (defaultProduct ? (defaultDayKey ? plannedQuantityForDay(defaultProduct, defaultDayKey) : scheduledQuantity(defaultProduct)) : null));
      setSelectedDayKey((current) => current ?? defaultDayKey);
      setPinRequired(false);
      setError("");
    } catch (reason: unknown) {
      if (!isCurrent()) return;
      if (reason instanceof ApiError && reason.code === "SARF39_PIN_REQUIRED") {
        await clearOfflineSnapshot().catch(() => undefined);
        if (!isCurrent()) return;
        setPinRequired(true);
        setError("");
        return;
      }
      if (reason instanceof ApiError && reason.code === "NETWORK_ERROR") {
        const cached = await readOfflineSnapshot().catch(() => null);
        if (!isCurrent()) return;
        if (cached) {
          try { parseSarf39Snapshot(cached); } catch {
            setError("Bu cihazdaki plan kopyası geçersiz. İnternet açıkken planı yeniden açın.");
            return;
          }
          const defaultDayKey = currentPlanDayKey(cached.products);
          const defaultProduct = (defaultDayKey
            ? cached.products.find((product) => plannedQuantityForDay(product, defaultDayKey) > 0)
            : null) ?? cached.products[0];
          setSnapshot(cached);
          setSelectedCode((current) => current || defaultProduct?.code || "");
          setCalculationQuantity((current) => current ?? (defaultProduct ? (defaultDayKey ? plannedQuantityForDay(defaultProduct, defaultDayKey) : scheduledQuantity(defaultProduct)) : null));
          setSelectedDayKey((current) => current ?? defaultDayKey);
          setPinRequired(false);
          setError("");
          return;
        }
        setError("Bu cihazda geçerli plan kopyası yok. İnternet açıkken PIN ile planı bir kez açın.");
        return;
      }
      setError(reason instanceof Error ? reason.message : "Sarf verisi yüklenemedi.");
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    lifecycle.current += 1;
    pinPending.current = false;
    setIsSubmittingPin(false);
    void loadSnapshot();
    return () => { mounted.current = false; generation.current += 1; lifecycle.current += 1; };
  }, [loadSnapshot]);

  const submitPin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!mounted.current || pinPending.current) return;
    const scope = lifecycle.current;
    const isCurrent = () => mounted.current && lifecycle.current === scope;
    pinPending.current = true;
    setPinError("");
    setIsSubmittingPin(true);
    try {
      const access = parseSarf39Access(await apiRequest<unknown>("/api/sarf39/access", { method: "POST", body: JSON.stringify({ pin }) }));
      if (!isCurrent()) return;
      await saveOfflineGrant(access.expiresAt).catch(() => undefined);
      if (!isCurrent()) return;
      setPin("");
      await loadSnapshot();
    } catch (reason) {
      if (!isCurrent()) return;
      setPinError(reason instanceof Error ? reason.message : "PIN doğrulanamadı.");
    } finally {
      if (isCurrent()) { pinPending.current = false; setIsSubmittingPin(false); }
    }
  };

  const products = snapshot?.products ?? [];
  const selected = products.find((product) => product.code === selectedCode) ?? products[0];
  const normalizedSearch = search.toLocaleLowerCase("tr-TR");
  const availableDays = useMemo(() => {
    const days = new Map<string, PlannedDay>();
    products.forEach((product) => product.dailyPlan.forEach((day) => days.set(day.key, day)));
    return [...days.values()].sort((left, right) => dayOrder.indexOf(left.key) - dayOrder.indexOf(right.key));
  }, [products]);
  const selectedDay = availableDays.find((day) => day.key === selectedDayKey);
  const filteredProducts = useMemo(
    () => products.filter((product) => {
      const matchesSearch = `${product.code} ${product.name} ${product.lineNames.join(" ")}`.toLocaleLowerCase("tr-TR").includes(normalizedSearch);
      return matchesSearch && (!selectedDayKey || plannedQuantityForDay(product, selectedDayKey) > 0);
    }),
    [normalizedSearch, products, selectedDayKey],
  );
  const tree = useMemo(() => selected ? buildTree(selected) : [], [selected]);
  const quantity = calculationQuantity ?? selected?.planQuantity ?? 0;

  const selectProduct = (product: Product) => {
    setSelectedCode(product.code);
    setCalculationQuantity(selectedDayKey ? plannedQuantityForDay(product, selectedDayKey) : scheduledQuantity(product));
    setSelectionPanelOpen(true);
  };

  const selectDay = (dayKey: string | null) => {
    setSelectedDayKey(dayKey);
    const dayProducts = dayKey ? products.filter((product) => plannedQuantityForDay(product, dayKey) > 0) : products;
    const nextProduct = dayProducts.find((product) => product.code === selectedCode) ?? dayProducts[0];
    if (!nextProduct) {
      setCalculationQuantity(0);
      return;
    }
    setSelectedCode(nextProduct.code);
    setCalculationQuantity(dayKey ? plannedQuantityForDay(nextProduct, dayKey) : scheduledQuantity(nextProduct));
    setSelectionPanelOpen(false);
  };

  return { snapshot, error, pinRequired, pin, setPin, pinError, isSubmittingPin, search, setSearch, setCalculationQuantity, selectedDayKey, selectionPanelOpen, setSelectionPanelOpen, submitPin, selected, availableDays, selectedDay, filteredProducts, tree, quantity, selectProduct, selectDay };
}
